import { useState } from 'react';
import { Alert, AIExplanation, UserRole } from '@cbm/shared';
import { Brain, Lightbulb, AlertTriangle, Shield, Wrench, Clock, Loader2, X } from 'lucide-react';

interface AIExplanationPanelProps {
  alert: Alert;
  userRole: UserRole;
  onClose?: () => void;
  className?: string;
}

export function AIExplanationPanel({
  alert,
  userRole,
  onClose,
  className = ''
}: AIExplanationPanelProps) {
  const [explanation, setExplanation] = useState<AIExplanation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestExplanation = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
      const response = await fetch(`${apiUrl}/assist/explain-alert`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          alertId: alert.alertId,
          userRole: userRole
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to get explanation: ${response.statusText}`);
      }

      const result = await response.json();
      if (result.success) {
        setExplanation(result.data);
      } else {
        throw new Error(result.error || 'Failed to get explanation');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleTitle = (role: UserRole) => {
    switch (role) {
      case 'PLANT': return 'Plant Operator';
      case 'OEM_SERVICE': return 'OEM Service';
      case 'OEM_ENGINEERING': return 'OEM Engineering';
      case 'QUALITY': return 'Quality Manager';
      default: return 'User';
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'text-green-600';
    if (confidence >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getConfidenceLabel = (confidence: number) => {
    if (confidence >= 0.8) return 'High';
    if (confidence >= 0.6) return 'Medium';
    return 'Low';
  };

  return (
    <div className={`bg-white border rounded-lg shadow-sm ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center space-x-2">
          <Brain className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-medium text-gray-900">AI Explanation</h3>
          <span className="text-sm text-gray-500">for {getRoleTitle(userRole)}</span>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="p-4">
        {/* Alert Summary */}
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <span className="text-sm font-medium text-gray-900">{alert.type}</span>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
              alert.severity === 'LATE' ? 'bg-red-100 text-red-800' :
              alert.severity === 'MID' ? 'bg-yellow-100 text-yellow-800' :
              'bg-blue-100 text-blue-800'
            }`}>
              {alert.severity}
            </span>
          </div>
          <p className="text-sm text-gray-600">{alert.summary}</p>
        </div>

        {/* Request Button */}
        {!explanation && !isLoading && !error && (
          <div className="text-center py-8">
            <button
              onClick={requestExplanation}
              className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Brain className="w-4 h-4" />
              <span>Get AI Explanation</span>
            </button>
            <p className="text-xs text-gray-500 mt-2">
              Get role-specific insights and recommendations
            </p>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-8">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-2" />
            <p className="text-sm text-gray-600">Analyzing alert...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <span className="text-sm font-medium text-red-800">Error</span>
            </div>
            <p className="text-sm text-red-700">{error}</p>
            <button
              onClick={requestExplanation}
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
            >
              Try again
            </button>
          </div>
        )}

        {/* Explanation Content */}
        {explanation && (
          <div className="space-y-4">
            {/* Confidence and Timestamp */}
            <div className="flex items-center justify-between text-xs text-gray-500">
              <div className="flex items-center space-x-2">
                <span>Confidence:</span>
                <span className={`font-medium ${getConfidenceColor(explanation.confidence)}`}>
                  {getConfidenceLabel(explanation.confidence)} ({Math.round(explanation.confidence * 100)}%)
                </span>
              </div>
              <div className="flex items-center space-x-1">
                <Clock className="w-3 h-3" />
                <span>{new Date(explanation.generatedAt).toLocaleString()}</span>
              </div>
            </div>

            {/* Main Explanation */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <Lightbulb className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-blue-900 mb-2">Explanation</h4>
                  <p className="text-sm text-blue-800">{explanation.explanation}</p>
                </div>
              </div>
            </div>

            {/* Likely Causes */}
            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-orange-900 mb-2">Likely Causes</h4>
                  <ul className="space-y-1">
                    {explanation.likelyCauses.map((cause, index) => (
                      <li key={index} className="text-sm text-orange-800 flex items-start space-x-2">
                        <span className="w-1 h-1 bg-orange-600 rounded-full mt-2 flex-shrink-0"></span>
                        <span>{cause}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Immediate Checks */}
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <Shield className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-yellow-900 mb-2">Immediate Checks</h4>
                  <ul className="space-y-1">
                    {explanation.immediateChecks.map((check, index) => (
                      <li key={index} className="text-sm text-yellow-800 flex items-start space-x-2">
                        <span className="w-1 h-1 bg-yellow-600 rounded-full mt-2 flex-shrink-0"></span>
                        <span>{check}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Containment Actions */}
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-red-900 mb-2">Containment Actions</h4>
                  <ul className="space-y-1">
                    {explanation.containmentActions.map((action, index) => (
                      <li key={index} className="text-sm text-red-800 flex items-start space-x-2">
                        <span className="w-1 h-1 bg-red-600 rounded-full mt-2 flex-shrink-0"></span>
                        <span>{action}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Preventive Measures */}
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <Wrench className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-green-900 mb-2">Preventive Measures</h4>
                  <ul className="space-y-1">
                    {explanation.preventiveMeasures.map((measure, index) => (
                      <li key={index} className="text-sm text-green-800 flex items-start space-x-2">
                        <span className="w-1 h-1 bg-green-600 rounded-full mt-2 flex-shrink-0"></span>
                        <span>{measure}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Token Usage (for debugging/monitoring) */}
            {explanation.tokenUsage.input > 0 && (
              <div className="text-xs text-gray-500 pt-2 border-t">
                <span>Token usage: {explanation.tokenUsage.input} input, {explanation.tokenUsage.output} output</span>
              </div>
            )}

            {/* Disclaimer */}
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-xs text-gray-600">
                <strong>Disclaimer:</strong> This AI-generated explanation is based on synthetic data and general patterns. 
                Always verify recommendations with domain experts and follow your organization's procedures.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Compact AI explanation button for integration into alert cards
interface AIExplanationButtonProps {
  alert: Alert;
  userRole: UserRole;
  onExplanationReceived?: (explanation: AIExplanation) => void;
  className?: string;
}

export function AIExplanationButton({
  alert,
  userRole,
  onExplanationReceived,
  className = ''
}: AIExplanationButtonProps) {
  const [isLoading, setIsLoading] = useState(false);

  const requestExplanation = async () => {
    setIsLoading(true);
    
    try {
      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
      const response = await fetch(`${apiUrl}/assist/explain-alert`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          alertId: alert.alertId,
          userRole: userRole
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to get explanation: ${response.statusText}`);
      }

      const result = await response.json();
      if (result.success) {
        onExplanationReceived?.(result.data);
      } else {
        throw new Error(result.error || 'Failed to get explanation');
      }
    } catch (err) {
      console.error('Failed to get AI explanation:', err);
      // Could show a toast notification here
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={requestExplanation}
      disabled={isLoading}
      className={`
        inline-flex items-center space-x-1 px-2 py-1 text-xs font-medium rounded
        ${isLoading 
          ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
          : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
        }
        transition-colors ${className}
      `}
    >
      {isLoading ? (
        <Loader2 className="w-3 h-3 animate-spin" />
      ) : (
        <Brain className="w-3 h-3" />
      )}
      <span>{isLoading ? 'Analyzing...' : 'AI Explain'}</span>
    </button>
  );
}